import React, { useState, useMemo, useEffect } from 'react';
import SelectButton from './SelectButton';
import { Dropdown, SearchBox } from 'tea-component';
import { useIntl } from 'umi';

import GroupCheckBox from './GroupCheckBox';
import {
  GroupMultiSelectPropsType,
  GroupOptionsItemType,
  GroupChildrenItemType,
} from './type';
import { getAllKeys, optionsToMap } from './util';

import './index.scss';

export default (props: GroupMultiSelectPropsType) => {
  const {
    options,
    searchPlaceholder,
    titleLabel,
    selectNameText,
    showNumber = 2,
    buttonItemRender = (element: any) => element,
    optionItemRender = (element: any) => element,
    optionItemFormatter = (element: any) => element,
    selectOptionsInfo,
    searchInfo,
    className,
    boxClassName,
    onChange,
    ...restProps
  } = props;

  const { formatMessage } = useIntl();

  // 原始静态数据，用于搜索比对
  const basicOptionsList = options;
  // 原始数据获取所有的key，用于实际筛选和button映射
  const basicOptionsKeys = useMemo(() => getAllKeys(options), [options]);
  // 原始数据转为map，用于button映射
  const basicOptionsMap = useMemo(() => optionsToMap(options), [options]);
  //实际筛选项
  const [selectOptions, setSelectOptions] =
    selectOptionsInfo || useState<Array<string>>(basicOptionsKeys);
  const changeSelectOptions = (value: Array<string>) => {
    setSelectOptions(value);
    onChange?.(value);
  };

  //搜索结果
  const [searchResultList, setSearchResultList] = useState<
    Array<GroupChildrenItemType>
  >([]);
  const [searchValue, setSearchValue] = searchInfo || useState('');
  // 传入的父子结构，用于渲染组
  const [optionsList, setOptionsList] = useState(options);
  // 同步数据，防止切换语言的时候会有文案的变化
  useEffect(() => {
    setOptionsList(options);
  }, [options]);

  // 全选 、 反选
  const getChangeAll = () => {
    let allList: Array<string> = [];
    optionsList?.forEach((items: GroupOptionsItemType) => {
      items.children?.forEach((item: GroupChildrenItemType) => {
        allList.push(item.value);
      });
    });
    selectOptions.length > 0
      ? changeSelectOptions([])
      : changeSelectOptions(allList);
  };

  // 筛选过滤
  const getSearchResult = (value: string) => {
    setSearchValue(value);

    // 搜索组
    const searchGroup = basicOptionsList.filter((item) => {
      // 全匹配
      return item.groupValue?.toLowerCase() == value.toLowerCase();
    });
    // 搜索列表
    const searchResult: Array<GroupOptionsItemType> = [];
    basicOptionsList.forEach((items) => {
      const list = items.children?.filter((item) => {
        // 部分匹配
        return item.text.toLowerCase().indexOf(value.toLowerCase()) != -1;
      });
      if (list && list.length > 0) {
        searchResult.push({
          groupKey: items.groupKey,
          groupValue: items.groupValue,
          children: list,
        });
      }
    });

    value == ''
      ? setOptionsList(basicOptionsList)
      : setOptionsList(searchGroup.length > 0 ? searchGroup : searchResult);

    // 返回搜索结果列表
    let searchResultList: Array<GroupChildrenItemType> = [];
    if (searchGroup.length > 0) {
      searchResultList = searchGroup[0].children || [];
    } else {
      searchResult?.forEach((items: GroupOptionsItemType) => {
        items.children?.forEach((item: GroupChildrenItemType) => {
          searchResultList.push(item);
        });
      });
    }
    return searchResultList;
  };

  return (
    <Dropdown
      className={`$db-group-multiselect ${className}`}
      overlayClassName="db-group-multiselect-overlay"
      onClose={() => {
        setSearchValue('');
        setSearchResultList(getSearchResult(''));
      }}
      button={
        <SelectButton
          selectNameText={selectNameText}
          formatMessage={formatMessage}
          selectLen={basicOptionsKeys?.length}
          options={selectOptions}
          showNumber={showNumber}
          buttonItemRender={buttonItemRender}
          basicOptionsMap={basicOptionsMap}
        />
      }
      size="m"
      clickClose={false}
      appearance="button"
      boxClassName={`${boxClassName}`}
      {...restProps}
    >
      {(close) => (
        <div className="db-group-multiselect-body">
          {/* 搜索 */}
          <SearchBox
            className="db-group-multiselect-search"
            simple
            value={searchValue}
            onChange={(e) => {
              setSearchResultList(getSearchResult(e));
            }}
            placeholder={searchPlaceholder}
          />
          {/* 全选 */}
          <div className="db-group-multiselect-flex">
            <div className="db-group-multiselect-text">
              {searchValue
                ? `${formatMessage({ id: 'Search Result' })} (${
                    searchResultList.length
                  })`
                : titleLabel}
            </div>
            <div
              className="db-group-multiselect-link"
              onClick={() => {
                getChangeAll();
              }}
            >
              {formatMessage({
                id: selectOptions.length > 0 ? 'Clear All' : 'Select All',
              })}
            </div>
          </div>
          {/* 状态 */}
          {/* <StatusTip status="loading" /> */}
          {/* 内容 */}
          <div className="db-group-multiselect-context">
            {/* 分组多选 */}
            {optionsList?.map((item: GroupOptionsItemType, index: number) => {
              return (
                <>
                  {item.groupKey && (
                    <GroupCheckBox
                      key={index}
                      data={item}
                      optionItemRender={optionItemRender}
                      selectOptions={selectOptions}
                      searchValue={searchValue}
                      optionItemFormatter={optionItemFormatter}
                      getChange={(value: React.SetStateAction<string[]>) => {
                        changeSelectOptions(value);
                      }}
                    />
                  )}
                </>
              );
            })}
          </div>
        </div>
      )}
    </Dropdown>
  );
};
